/* eslint-disable no-console */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { saveCampaignServiceAPI } from 'services/saveCampaign.service';
import { chatWidgetActions } from 'store/actions';
import { useScrollLock } from './useScrollLock';

const WIDGET_SCRIPT_SRC = window.AI_AGENT_WIDGET_URL;
const WIDGET_DEBUG_MODE = window.WIDGET_DEBUGMODE;
const WIDGET_ROUTE = '/discovery/manage';
const EDIT_URL_TEMPLATE = '/Discovery/Campaign/Edit/{campaignId}';

const closeWidget = () => {
  if (!window.AppnextChat) return;
  if (typeof window.AppnextChat.close === 'function') {
    try {
      window.AppnextChat.close();
      return;
    } catch (err) {
      console.error('[AppnextChat] close failed', err);
    }
  }
  // Fallback: click the hidden toggle to dismiss the panel.
  const toggle = document.querySelector('.cai-widget-toggle');
  if (toggle) {
    toggle.style.setProperty('display', '', '');
    toggle.click();
    toggle.style.setProperty('display', 'none', 'important');
  }
};

const buildFormData = (formFields = {}) => {
  const formData = new FormData();
  Object.entries(formFields).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (typeof File !== 'undefined' && value instanceof File) {
      formData.append(key, value);
    } else if (typeof Blob !== 'undefined' && value instanceof Blob) {
      formData.append(key, value);
    } else if (typeof value === 'object') {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, String(value));
    }
  });
  return formData;
};

const extractErrorMessage = (error) =>
  error?.data?.message ||
  error?.data?.Message ||
  error?.message ||
  (typeof error?.data === 'string' ? error.data : null) ||
  'Failed to save campaign';

export const ChatWidgetHost = () => {
  const location = useLocation();
  const isWidgetRoute = location.pathname.toLowerCase() === WIDGET_ROUTE;
  // Defer init until the access token is in Redux — `localStorageMiddleware`
  // writes localStorage['auth'] in the same dispatch that sets accessToken,
  // so this is a lossless proxy for "token is in localStorage".
  const accessToken = useSelector((state) => state.auth.accessToken);
  const canInit = !!accessToken;
  const [isOpen, setIsOpen] = useState(false);
  const showOverlay = isOpen && isWidgetRoute;
  const dispatch = useDispatch();

  const [saveCampaignData] = saveCampaignServiceAPI.useSaveCampaignDataMutation();
  const [saveAndStartCampaign] = saveCampaignServiceAPI.useSaveAndStartCampaignMutation();

  // The widget registers `onSaveCampaign` once at init; capture latest
  // triggers in a ref so the callback always sees current state.
  const triggersRef = useRef({ saveCampaignData, saveAndStartCampaign });
  triggersRef.current = { saveCampaignData, saveAndStartCampaign };

  const onSaveCampaign = useCallback(async (args) => {
    if (!args || !args.callbacks) return;
    const { payload, startCampaign, callbacks } = args;
    try {
      const formData = buildFormData(payload?.formFields);
      const trigger = startCampaign
        ? triggersRef.current.saveAndStartCampaign
        : triggersRef.current.saveCampaignData;
      const res = await trigger(formData);

      if (res?.error) {
        callbacks.onError(extractErrorMessage(res.error));
        return;
      }
      const cmId = res?.data?.cm_id ?? res?.data?.campaignId ?? res?.data?.id;
      if (cmId === undefined || cmId === null || cmId === '') {
        callbacks.onError('Server did not return a campaign id');
        return;
      }
      callbacks.onSuccess(String(cmId));
    } catch (err) {
      callbacks.onError(err?.message || 'Unexpected error');
    }
  }, []);

  useEffect(() => {
    if (!canInit) return undefined;

    let cancelled = false;
    let initialized = false;

    const markUnavailable = () => {
      if (cancelled) return;
      dispatch(chatWidgetActions.setChatWidgetUnavailable());
    };

    const initWidget = () => {
      if (cancelled || initialized) return;
      if (!window.AppnextChat) {
        markUnavailable();
        return;
      }
      try {
        window.AppnextChat.init({
          // Sourced from serverConfig.js (window.WIDGET_DEBUGMODE) so it can be
          // toggled without recompiling. Takes effect once the widget bundle
          // forwards `debugMode` from init() (see WIDGET_DEBUGMODE_FIX.md).
          debugMode: WIDGET_DEBUG_MODE,
          editCampaignUrlTemplate: EDIT_URL_TEMPLATE,
          onOpen: () => {
            setIsOpen(true);
            console.log('[AppnextChat] opened');
          },
          onClose: () => {
            setIsOpen(false);
            console.log('[AppnextChat] closed');
          },
          onSaveCampaign,
        });
        initialized = true;
        dispatch(chatWidgetActions.setChatWidgetAvailable());
        console.log('[AppnextChat] initialized from React host');
      } catch (err) {
        console.error('[AppnextChat] init failed', err);
        markUnavailable();
      }
    };

    if (window.AppnextChat) {
      initWidget();
    } else {
      const existing = document.querySelector(`script[src="${WIDGET_SCRIPT_SRC}"]`);
      if (existing) {
        existing.addEventListener('load', initWidget, { once: true });
        existing.addEventListener('error', markUnavailable, { once: true });
      } else {
        const script = document.createElement('script');
        script.src = WIDGET_SCRIPT_SRC;
        script.async = true;
        script.onload = initWidget;
        script.onerror = () => {
          console.warn('[AppnextChat] widget script failed to load');
          markUnavailable();
        };
        document.body.appendChild(script);
      }
    }

    return () => {
      cancelled = true;
      if (initialized && window.AppnextChat?.destroy) {
        try {
          window.AppnextChat.destroy();
        } catch (err) {
          console.error('[AppnextChat] destroy failed', err);
        }
      }
    };
  }, [canInit, onSaveCampaign]);

  // Hide (don't destroy) the widget on every route change, so prior
  // conversation state is preserved when the user returns to Discovery/Manage.
  useEffect(() => {
    closeWidget();
    setIsOpen(false);
  }, [location.pathname]);

  // Drive widget visibility + click-blocking via two classes on <html>:
  //   cai-modal-open   → panel visible, page beneath click-blocked
  //   cai-widget-hidden → panel forced display:none (DOM/state preserved)
  // CSS rules live in public/index.html.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('cai-modal-open', showOverlay);
    root.classList.toggle('cai-widget-hidden', !showOverlay);
    return () => {
      root.classList.remove('cai-modal-open');
      root.classList.remove('cai-widget-hidden');
    };
  }, [showOverlay]);

  // Real scroll lock: freezes the background page (wheel/trackpad/keyboard/
  // scrollbar) while the modal is open, restoring scroll position on close.
  // Never intercepts input inside #appnext-chat-widget — see useScrollLock.js.
  useScrollLock(showOverlay, '#appnext-chat-widget');

  return null;
};

export default ChatWidgetHost;
